import { React, useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { TailSpin } from 'react-loader-spinner';
import SftGlobalData from '../../library/SftGlobalData';
import Colors from '../../library/Colors';

// BPOST sub-types.
const TYPE_LABEL_KEY = {
    '42501': 'pickupType_postOffice',
    '42502': 'pickupType_postPoint',
    '42503': 'pickupType_packstation',
    '42504': 'pickupType_shop',
    '42505': 'pickupType_kariboo',
};
const ALL_TYPE_CODES = ['42501', '42502', '42503', '42504', '42505'];
// Kariboo (42505) is off by default — it's a third-party network and most shops don't use it.
const DEFAULT_TYPE_CODES = ['42501', '42502', '42503', '42504'];

// MyParcel pickup types (no bpost-style sub-network; just point vs parcel locker).
const MYPARCEL_TYPE_LABEL_KEY = {
    '42601': 'pickupType_pickupPoint',
    '42602': 'pickupType_parcelLocker',
};
const MYPARCEL_TYPE_CODES = ['42601', '42602'];

// Sendcloud: single "pickup point" type (the API has no reliable point/locker split), so no filter chips.
const SENDCLOUD_TYPE_LABEL_KEY = {
    '42701': 'pickupType_pickupPoint',
};
const SENDCLOUD_TYPE_CODES = [];

const isMyParcelProvider = (provider) => String(provider || '').startsWith('myparcel');
const isSendcloudProvider = (provider) => String(provider || '').startsWith('sendcloud');

// Per-point carrier badge: prettier label + brand colour, so it's clear which provider a pickup
// point belongs to (and future-proof if we ever mix carriers in one list).
const CARRIER_LABELS = { postnl: 'PostNL', bpost: 'bpost', dpd: 'DPD', dhl: 'DHL', ups: 'UPS' };
const CARRIER_COLORS = { postnl: '#EC7A00', bpost: '#E5133C', dpd: '#DC0032', dhl: '#D40511', ups: '#644117' };

const PickupPointPicker = (props) => {
    // Provider-aware type sets (the parent passes `provider`, e.g. 'direct_bpost' | 'myparcel_postnl' | 'sendcloud_dpd').
    const isMyParcel  = isMyParcelProvider(props.provider);
    const isSendcloud = isSendcloudProvider(props.provider);
    // Fallback carrier for the badge: MyParcel points carry their own `carrier`, but bpost-direct
    // points don't — derive it from the provider (e.g. 'direct_bpost' -> 'bpost').
    const providerCarrier = String(props.provider || '').split('_').slice(1).join('_').toLowerCase();
    const typeLabelKeys = isSendcloud ? SENDCLOUD_TYPE_LABEL_KEY : (isMyParcel ? MYPARCEL_TYPE_LABEL_KEY : TYPE_LABEL_KEY);
    const allTypeCodes  = isSendcloud ? SENDCLOUD_TYPE_CODES     : (isMyParcel ? MYPARCEL_TYPE_CODES     : ALL_TYPE_CODES);
    const [searchZip, setSearchZip] = useState(props.customer?.zip ?? '');
    const [searchStreet, setSearchStreet] = useState(props.customer?.street_name ?? '');
    const [searchNumber, setSearchNumber] = useState(props.customer?.house_number ?? '');
    const [points, setPoints] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // Per-field validation so we can show a red outline on empty required fields when Search is pressed.
    const [fieldErrors, setFieldErrors] = useState({ zip: false, street: false, number: false });
    const [initialSearchDone, setInitialSearchDone] = useState(false);
    // Optional type filter. BPOST default: everything except Kariboo. MyParcel default: both. Sendcloud: none (single type).
    const [selectedTypes, setSelectedTypes] = useState(
        isSendcloudProvider(props.provider) ? SENDCLOUD_TYPE_CODES
            : (isMyParcelProvider(props.provider) ? MYPARCEL_TYPE_CODES : DEFAULT_TYPE_CODES)
    );

    const accentClass = props.theme === 'light' ? 'light_accent' : 'dark_accent_darker';

    const toggleType = (code) => {
        setSelectedTypes(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
    };

    const runSearch = (overrides = {}, autoSelectFromPrefill = false, showFieldErrors = true) => {
        const payload = {
            key: props.api,
            provider: props.provider,
            country: overrides.country ?? props.customer?.country ?? props.country,
            zip: overrides.zip ?? searchZip,
            street: overrides.street ?? searchStreet,
            street_number: overrides.number ?? searchNumber,
            language: (props.lang || 'nl').toUpperCase(),
            // Send the type filter only when it's a strict subset; sending all is equivalent to omitting it.
            types: selectedTypes.length > 0 && selectedTypes.length < allTypeCodes.length ? selectedTypes : undefined,
        };
        // MyParcel requires the full address (zip + street + number); other providers accept zip OR street.
        const missing = isMyParcel
            ? {
                zip: !String(payload.zip ?? '').trim(),
                street: !String(payload.street ?? '').trim(),
                number: !String(payload.street_number ?? '').trim(),
            }
            : {
                zip: !String(payload.zip ?? '').trim() && !String(payload.street ?? '').trim(),
                street: !String(payload.zip ?? '').trim() && !String(payload.street ?? '').trim(),
                number: false,
            };
        if (missing.zip || missing.street || missing.number) {
            if (showFieldErrors) setFieldErrors(missing);
            setError(props.t(isMyParcel ? 'searchPickupPointHelpMyParcel' : 'searchPickupPointHelp'));
            return;
        }
        setFieldErrors({ zip: false, street: false, number: false });
        setLoading(true);
        setError('');
        SftGlobalData.post('shipping&action=fetchPickupPoints', payload).then(response => {
            const data = response?.data;
            const list = Array.isArray(data?.points) ? data.points : [];
            setPoints(list);
            setLoading(false);
            if (list.length === 0) {
                setError(props.t('noPickupPointsFound'));
            } else if (autoSelectFromPrefill && props.selectedPickupId) {
                // When the search was triggered by a prefilled pickup_id (e.g. from a receipt),
                // auto-emit the matching point so the parent's "Selected pickup point" summary renders.
                const match = list.find(p => String(p.id) === String(props.selectedPickupId));
                if (match) handleSelect(match);
            }
        }).catch(() => {
            setLoading(false);
            setError(props.t('noPickupPointsFound'));
            setPoints([]);
        });
    };

    // Initial search when a pickup_id is prefilled from a receipt.
    useEffect(() => {
        if (!initialSearchDone && props.selectedPickupId) {
            setInitialSearchDone(true);
            runSearch({}, true, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedPickupId]);

    const handleSelect = (point) => {
        const selection = {
            pickup_id: String(point.id ?? ''),
            pickup_type: String(point.pickup_type ?? ''),
            pickup_name: String(point.name ?? ''),
            pickup_street: String(point.street ?? ''),
            pickup_number: String(point.number ?? ''),
            pickup_zip: String(point.zip ?? ''),
            pickup_city: String(point.city ?? ''),
            pickup_country: String(point.country || 'BE'),
            pickup_bpost_type: point.bpost_type,
        };
        // MyParcel needs these at ship time (delivery_type 4 + pickup object).
        if (point.location_code !== undefined) {
            selection.pickup_location_code = String(point.location_code ?? '');
            selection.pickup_retail_network_id = String(point.retail_network_id ?? '');
            selection.pickup_carrier = String(point.carrier ?? '');
            selection.pickup_cc = String(point.cc ?? point.country ?? '');
        }
        props.onSelect(selection);
    };

    const typeLabel = (code) => {
        const key = typeLabelKeys[String(code)];
        return key ? props.t(key) : '';
    };

    const selectedId = props.selectedPickupId ? String(props.selectedPickupId) : '';

    return (
        <Box className={'text_left margin_top_10px _100perc'}>
            <Typography variant="body2" sx={{ mb: 1 }}>{props.t(isMyParcel ? 'searchPickupPointHelpMyParcel' : 'searchPickupPointHelp')}</Typography>
            <Stack
                direction={props.screenWidth < 700 ? 'column' : 'row'}
                spacing={1}
                sx={{ mb: 1, alignItems: props.screenWidth < 700 ? 'stretch' : 'center' }}
            >
                <TextField
                    label={props.t('zip')}
                    value={searchZip}
                    onChange={(e) => { setSearchZip(e.target.value); if (fieldErrors.zip) setFieldErrors(prev => ({ ...prev, zip: false })); }}
                    error={fieldErrors.zip}
                    className={accentClass}
                    sx={{ minWidth: 110 }}
                />
                <TextField
                    label={props.t('streetname')}
                    value={searchStreet}
                    onChange={(e) => { setSearchStreet(e.target.value); if (fieldErrors.street) setFieldErrors(prev => ({ ...prev, street: false })); }}
                    error={fieldErrors.street}
                    className={accentClass}
                    fullWidth
                />
                <TextField
                    label={props.t('housenumber')}
                    value={searchNumber}
                    onChange={(e) => { setSearchNumber(e.target.value); if (fieldErrors.number) setFieldErrors(prev => ({ ...prev, number: false })); }}
                    error={fieldErrors.number}
                    className={accentClass}
                    sx={{ width: props.screenWidth < 700 ? '100%' : 140 }}
                />
                <Button
                    variant="contained"
                    onClick={() => runSearch()}
                    disabled={loading || selectedTypes.length === 0}
                    sx={{
                        backgroundColor: Colors.sft_blue,
                        '&:hover': { backgroundColor: Colors.sft_blue_dark || Colors.sft_blue },
                        whiteSpace: 'nowrap',
                        height: 56,
                        minWidth: props.screenWidth < 700 ? '100%' : 180,
                    }}
                >
                    {props.t('searchPickupPoint')}
                </Button>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
                {allTypeCodes.map((code) => {
                    const labelKey = typeLabelKeys[code];
                    const active = selectedTypes.includes(code);
                    return (
                        <Chip
                            key={code}
                            label={labelKey ? props.t(labelKey) : code}
                            onClick={() => toggleType(code)}
                            variant={active ? 'filled' : 'outlined'}
                            color={active ? 'primary' : 'default'}
                            size="small"
                            sx={{
                                cursor: 'pointer',
                                ...(active ? { backgroundColor: Colors.sft_blue, color: '#fff' } : {}),
                            }}
                        />
                    );
                })}
            </Stack>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <TailSpin height="32" width="32" color={Colors.sft_blue} ariaLabel="loading" />
                </Box>
            )}

            {!loading && error && (
                <Typography variant="body2" sx={{ color: 'text.secondary', my: 1 }}>
                    {error}
                </Typography>
            )}

            {!loading && points.length > 0 && (
                <Box sx={{ maxHeight: 560, overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
                    <Stack spacing={1.5} sx={{ width: '100%' }}>
                        {points.map((point) => {
                            const isSelected = String(point.id) === selectedId;
                            const street = String(point.street ?? '');
                            const number = String(point.number ?? '');
                            const zip = String(point.zip ?? '');
                            const city = String(point.city ?? '');
                            const name = String(point.name ?? '');
                            const label = typeLabel(point.pickup_type);
                            const carrier = String(point.carrier || providerCarrier || '').toLowerCase();
                            const carrierLabel = CARRIER_LABELS[carrier] || (carrier ? carrier.toUpperCase() : '');
                            const carrierColor = CARRIER_COLORS[carrier] || Colors.sft_blue;
                            return (
                                <Card
                                    key={point.id}
                                    variant="outlined"
                                    onClick={() => handleSelect(point)}
                                    sx={{
                                        cursor: 'pointer',
                                        borderColor: isSelected ? Colors.sft_blue : undefined,
                                        borderWidth: isSelected ? 2 : 1,
                                        backgroundColor: 'transparent',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        transition: 'border-color 0.15s ease',
                                        '&:hover': { borderColor: Colors.sft_blue },
                                    }}
                                >
                                    <CardContent sx={{
                                        p: 2,
                                        '&:last-child': { pb: 2 },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        minWidth: 0,
                                    }}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            flexWrap: 'wrap',
                                            minWidth: 0,
                                        }}>
                                            <Typography
                                                component="div"
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: 600,
                                                    lineHeight: 1.3,
                                                    flex: '1 1 auto',
                                                    minWidth: 0,
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'anywhere',
                                                }}
                                            >
                                                {name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, flexWrap: 'wrap' }}>
                                                {carrierLabel && (
                                                    <Chip
                                                        size="small"
                                                        label={carrierLabel}
                                                        sx={{
                                                            height: 24,
                                                            flexShrink: 0,
                                                            backgroundColor: carrierColor,
                                                            color: '#fff',
                                                            fontWeight: 600,
                                                            '& .MuiChip-label': { px: 1 },
                                                        }}
                                                    />
                                                )}
                                                {label && (
                                                    <Chip size="small" label={label} sx={{ height: 24, flexShrink: 0 }} />
                                                )}
                                            </Box>
                                        </Box>
                                        <Typography
                                            component="div"
                                            variant="body1"
                                            sx={{ lineHeight: 1.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                                        >
                                            {street}{street && number ? ' ' : ''}{number}
                                        </Typography>
                                        <Typography
                                            component="div"
                                            variant="body1"
                                            sx={{ lineHeight: 1.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                                        >
                                            {zip}{zip && city ? ' ' : ''}{city}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

export default PickupPointPicker;
